import React, { FC, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import BusinessesSummary from 'components/BusinessesSummary/BusinessesSummary'
import { BaseTableOverview, CurrentZone } from 'utils/types'
import { ServiceProvidersEntityForTable } from './types'
import { useVerticalOverviewQuery } from 'hooks/useVerticalOverviewQuery'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import { useDebounce } from 'hooks/useDebounce'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import { filtersInitialState } from './ServiceProvidersTablePage.config'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import Search from 'UI/Search/Search'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { keysForEmptyColumnsRequest, serviceProvidersColumns } from './utils'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'

const ServiceProvidersTablePage: FC = () => {
  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.serviceProvidersIndices,
    currentQueryKey: 'serviceProviders-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData, isLoading: isLoadingOverview } = useVerticalOverviewQuery<BaseTableOverview>({
    verticalIndices: currentZone?.serviceProvidersIndices,
    currentQueryKey: 'serviceProviders-overview-key',
    searchValue: searchValueDebounced,
    filters: activeFiltersDebounced,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<ServiceProvidersEntityForTable>({
      verticalIndices: currentZone?.serviceProvidersIndices,
      currentQueryKey: 'serviceProviders-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      filters: activeFiltersDebounced,
      pageSize: PAGE_SIZE,
    })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.serviceProvidersIndices,
    currentQueryKey: 'serviceProviders-filter-options-key',
    filtersInitialState,
  })

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      <section className={styles.summary} id={'businessSummary'} ref={statisticsActivityRef}>
        <BusinessesSummary
          isLoading={isLoadingOverview}
          summary={{
            entityCounts: overviewData?.summary.entitiesCount,
            incomeCounts: overviewData?.summary.incomeCounts,
            topIndustries: overviewData?.summary.platforms.map(({ source, count }) => [source, count]),
            industriesCount: overviewData?.summary.platforms.reduce((acc, { count }) => acc + count, 0),
          }}
          isInfluencers
        />
      </section>
      {isLoading ? (
        <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
          <Spin size='large' />
        </Flex>
      ) : isError ? (
        <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
          Something went wrong
        </Alert>
      ) : (
        <>
          {emptyColumnsIsFetched && (
            <VerticalTable
              pageSize={PAGE_SIZE}
              enableColumnPicker
              dataSource={verticalEntities as any[]}
              columns={serviceProvidersColumns}
              containerRef={containerRef}
              additionalRefs={[statisticsActivityRef, navigationRef]}
              fetchNextPage={fetchNextPage}
              total={overviewData?.total || 0}
              loading={isFetching}
              disabledColumns={emptyColumnsData}
              onSelectionChange={setSelectedIds}
              removeFromPickerColumns={[COLUMNS_IDS.profileNames]}
              rowClassName={styles.tableRow}
              onRow={record => {
                return {
                  onClick: () => {
                    window.open(`${window.location.origin}/vertical/service-providers/${record.id}`, '_blank')
                  },
                }
              }}
              onChange={(pagination, filters, sorter) => {
                if (!Array.isArray(sorter)) {
                  if (sorter.order) {
                    setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                  } else {
                    setSorting([])
                  }
                }
              }}
            >
              <div className={styles.counter}>
                {`Service Providers: `}
                <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
              </div>
              <VerticalTableFilters
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filtersInitialState={filtersInitialState}
                filterOptions={filterOptions}
                filterAdditionalData={[]}
              />
              <Search
                placeholder='input search text'
                enterButton
                allowClear
                style={{ width: 264 }}
                loading={isFetching}
                onSearch={newSearchValue => setSearchValue(newSearchValue)}
              />
              <ExportCSVTableVertical
                zoneIndex={currentZone?.serviceProvidersIndices}
                csvFileName='ServiceProviders'
                searchValue={searchValue}
                selectedIds={selectedIds}
                sorting={sorting}
              />
            </VerticalTable>
          )}
        </>
      )}
    </div>
  )
}

export default ServiceProvidersTablePage
